import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';
import LeadsLineChart from './components/LeadsLineChart';
import RevenueAreaChart from './components/RevenueAreaChart';
import SourcePieChart from './components/SourcePieChart';
import TopDestinationsBar from './components/TopDestinationsBar';
import VendorPerformanceBar from './components/VendorPerformanceBar';
import LocationConversionTable from './components/LocationConversionTable';
import DateFilterBar from './components/DateFilterBar';
import AccessDenied from '../AccessDenied';

const ANALYTICS_TIMEZONE = 'Asia/Kolkata';
const DEFAULT_GOOGLE_LEAD_PRICE = 60;
const DEFAULT_META_LEAD_PRICE = 15;

const formatDate = (date) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: ANALYTICS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  return formatter.format(date);
};

const getTimeZoneNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: ANALYTICS_TIMEZONE }));

const getLastNDaysRange = (days) => {
  const today = getTimeZoneNow();
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));

  return {
    startDate: formatDate(start),
    endDate: formatDate(today)
  };
};

const getThisMonthRange = () => {
  const today = getTimeZoneNow();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    startDate: formatDate(start),
    endDate: formatDate(today)
  };
};

const getTodayRange = () => {
  const today = getTimeZoneNow();
  return {
    startDate: formatDate(today),
    endDate: formatDate(today)
  };
};

const getYesterdayRange = () => {
  const today = getTimeZoneNow();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return {
    startDate: formatDate(yesterday),
    endDate: formatDate(yesterday)
  };
};

const getLastMonthRange = () => {
  const today = getTimeZoneNow();
  const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

  return {
    startDate: formatDate(firstDayLastMonth),
    endDate: formatDate(lastDayLastMonth)
  };
};

const LeadAnalytics = () => {
  const { user, hasPermission } = useAuth();

  const defaultRange = useMemo(() => getLastNDaysRange(30), []);
  const [draftStartDate, setDraftStartDate] = useState(defaultRange.startDate);
  const [draftEndDate, setDraftEndDate] = useState(defaultRange.endDate);
  const [draftGooglePrice, setDraftGooglePrice] = useState(DEFAULT_GOOGLE_LEAD_PRICE);
  const [draftMetaPrice, setDraftMetaPrice] = useState(DEFAULT_META_LEAD_PRICE);
  const [filters, setFilters] = useState({
    ...defaultRange,
    googlePrice: DEFAULT_GOOGLE_LEAD_PRICE,
    metaPrice: DEFAULT_META_LEAD_PRICE
  });
  const [activeQuickFilter, setActiveQuickFilter] = useState('last30');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analyticsData, setAnalyticsData] = useState({
    summary: {},
    charts: {
      leadsOverTime: [],
      revenueOverTime: [],
      leadsBySource: [],
      topDestinations: [],
      topSourceLocations: [],
      locationConversion: [],
      vendorPerformance: []
    }
  });

  const fetchLeadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.get('lead-analytics', {
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        googlePrice: filters?.googlePrice,
        metaPrice: filters?.metaPrice
      });

      if (!response?.status) {
        const message = response?.message || 'Failed to load lead analytics';
        setError(message);
        toast.error(message);
        return;
      }

      setAnalyticsData(response?.data || {});
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to load lead analytics';
      setError(message);
      toast.error(message);
      console.error('Lead analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLeadAnalytics();
  }, [fetchLeadAnalytics]);

  const applyDateRange = () => {
    if (!draftStartDate || !draftEndDate) {
      toast.error('Please select both start and end date');
      return;
    }

    if (new Date(draftStartDate) > new Date(draftEndDate)) {
      toast.error('Start date cannot be greater than end date');
      return;
    }

    const googlePrice = Number(draftGooglePrice);
    const metaPrice = Number(draftMetaPrice);

    if (!Number.isFinite(googlePrice) || googlePrice < 0 || !Number.isFinite(metaPrice) || metaPrice < 0) {
      toast.error('Please enter valid Google and Meta prices (0 or greater)');
      return;
    }

    setFilters({
      startDate: draftStartDate,
      endDate: draftEndDate,
      googlePrice,
      metaPrice
    });
    setActiveQuickFilter('');
  };

  const handleQuickFilter = (type) => {
    const range =
      type === 'today'
        ? getTodayRange()
        : type === 'yesterday'
          ? getYesterdayRange()
          : type === 'lastMonth'
            ? getLastMonthRange()
            : type === 'last7'
              ? getLastNDaysRange(7)
              : type === 'thisMonth'
                ? getThisMonthRange()
                : getLastNDaysRange(30);

    setDraftStartDate(range.startDate);
    setDraftEndDate(range.endDate);

    setFilters({
      ...range,
      googlePrice: Number(draftGooglePrice),
      metaPrice: Number(draftMetaPrice)
    });
    setActiveQuickFilter(type);
  };

  const clearDateRange = () => {
    setDraftStartDate(defaultRange.startDate);
    setDraftEndDate(defaultRange.endDate);
    setDraftGooglePrice(DEFAULT_GOOGLE_LEAD_PRICE);
    setDraftMetaPrice(DEFAULT_META_LEAD_PRICE);
    setFilters({
      ...defaultRange,
      googlePrice: DEFAULT_GOOGLE_LEAD_PRICE,
      metaPrice: DEFAULT_META_LEAD_PRICE
    });
    setActiveQuickFilter('last30');
  };

  const summary = analyticsData?.summary || {};

  if (user?.role === 'staff' && !hasPermission('manage_travel_post', 'lead_analytics', 'view')) {
    return <AccessDenied />;
  }

  const charts = analyticsData?.charts || {};
  const unlockVsTotalLeadsPercentage = Number(summary?.unlockVsTotalLeadsPercentage || 0);
  const replacementVsTotalUnlockPercentage = Number(summary?.replacementVsTotalUnlockPercentage || 0);
  const saleVsTotalLeadsPercentage = Number(summary?.conversionRate || 0);
  const sourceLeadPricing = summary?.sourceLeadPricing || {
    google: DEFAULT_GOOGLE_LEAD_PRICE,
    meta: DEFAULT_META_LEAD_PRICE
  };

  const sourceBreakdown = (charts?.leadsBySource || []).reduce(
    (acc, item) => {
      const label = (item?.label || '').toString().trim().toLowerCase();
      const value = Number(item?.value || 0);

      if (label === 'meta') {
        acc.meta += value;
      } else if (label === 'google') {
        acc.google += value;
      } else {
        acc.other += value;
      }

      return acc;
    },
    { google: 0, meta: 0, other: 0 }
  );

  const totalLeads = Number(summary?.totalLeads || 0);
  const totalRevenue = Number(summary?.totalRevenue || 0);
  const totalUnlockAmount = Number(summary?.totalUnlockAmount || 0);
  const acceptedReplacementAmount = Number(summary?.acceptedReplacementAmount || 0);

  const estimatedLeadValue =
    sourceBreakdown.google * Number(sourceLeadPricing.google || 0) +
    sourceBreakdown.meta * Number(sourceLeadPricing.meta || 0);

  const googleLeadPercentage = totalLeads > 0 ? (sourceBreakdown.google / totalLeads) * 100 : 0;
  const metaLeadPercentage = totalLeads > 0 ? (sourceBreakdown.meta / totalLeads) * 100 : 0;
  const otherLeadPercentage = totalLeads > 0 ? (sourceBreakdown.other / totalLeads) * 100 : 0;
  const aiScoreThreshold = Number(summary?.aiScoreThreshold || 50);
  const aiFinalScoreBelow50Leads = Number(summary?.aiFinalScoreBelow50Leads || 0);
  const aiFinalScoreRemainingLeads = Number(summary?.aiFinalScoreRemainingLeads || 0);
  const aiFinalScoreBelow50Percentage = Number(summary?.aiFinalScoreBelow50Percentage || 0);
  const aiFinalScoreRemainingPercentage = Number(summary?.aiFinalScoreRemainingPercentage || 0);
  const valueRealisationPercentage = estimatedLeadValue > 0 ? (totalRevenue / estimatedLeadValue) * 100 : 0;
  const profitAmount = totalRevenue - estimatedLeadValue;
  const profitPercentage = estimatedLeadValue > 0 ? (Math.abs(profitAmount) / estimatedLeadValue) * 100 : 0;
  const profitPositive = profitAmount >= 0;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="p-0 space-y-6">
        <DateFilterBar
          draftStartDate={draftStartDate}
          draftEndDate={draftEndDate}
          draftGooglePrice={draftGooglePrice}
          draftMetaPrice={draftMetaPrice}
          onStartDateChange={setDraftStartDate}
          onEndDateChange={setDraftEndDate}
          onGooglePriceChange={setDraftGooglePrice}
          onMetaPriceChange={setDraftMetaPrice}
          onApply={applyDateRange}
          onClear={clearDateRange}
          onQuickFilter={handleQuickFilter}
          activeQuickFilter={activeQuickFilter}
          onRefresh={fetchLeadAnalytics}
          loading={loading}
        />

        {error ? (
          <div className="rounded-2xl bg-red-50 border border-red-100 text-red-700 px-4 py-3 text-sm">{error}</div>
        ) : null}

        {loading ? (
          <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#009ef7] mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading analytics...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-[28px] bg-white shadow-sm border border-gray-100 p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                <div className="rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Leads Collected</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">Google and Meta split</div>
                    </div>
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700 text-sm">
                      <i className="bi bi-people"></i>
                    </span>
                  </div>
                  <div className="mt-4 text-xl font-semibold text-gray-900">{summary?.totalLeads ?? 0}</div>
                  <div className="mt-3 space-y-1.5 text-[11px]">
                    <div className="flex items-center justify-between text-gray-700">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-red-100 text-red-600 text-[11px] font-semibold">G</span>
                        <span className="font-medium">Google</span>
                      </div>
                      <span className="font-semibold text-gray-900">{sourceBreakdown.google} ({googleLeadPercentage.toFixed(1)}%)</span>
                    </div>
                    <div className="flex items-center justify-between text-gray-700">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-sky-100 text-sky-600 text-[11px] font-semibold">M</span>
                        <span className="font-medium">Meta</span>
                      </div>
                      <span className="font-semibold text-gray-900">{sourceBreakdown.meta} ({metaLeadPercentage.toFixed(1)}%)</span>
                    </div>
                    <div className="flex items-center justify-between text-gray-700">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-emerald-100 text-emerald-600 text-[11px] font-semibold">O</span>
                        <span className="font-medium">Organic</span>
                      </div>
                      <span className="font-semibold text-gray-900">{sourceBreakdown.other} ({otherLeadPercentage.toFixed(1)}%)</span>
                    </div>
                    <div className="flex items-center justify-between text-red-700">
                      <span className="font-medium">AI Score &lt; {aiScoreThreshold}</span>
                      <span className="font-semibold text-red-700">{aiFinalScoreBelow50Leads} ({aiFinalScoreBelow50Percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="flex items-center justify-between text-green-700">
                      <span className="font-medium">AI Score &ge; {aiScoreThreshold}</span>
                      <span className="font-semibold text-green-900">{aiFinalScoreRemainingLeads} ({aiFinalScoreRemainingPercentage.toFixed(1)}%)</span>
                    </div>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"><span>Est. Value Rs</span><span className="text-[13px] font-bold">{estimatedLeadValue.toFixed(2)}</span></div>
                </div>

                <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Leads Unlocked</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">Leads vendors opened</div>
                    </div>
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 text-sm">
                      <i className="bi bi-unlock"></i>
                    </span>
                  </div>
                  <div className="mt-4 text-xl font-semibold text-gray-900">{summary?.soldLeads ?? 0}</div>
                  <div className="mt-3 space-y-1.5 text-[11px]">
                    <div className="flex items-center justify-between text-gray-700">
                      <span className="font-medium">Lead Sale</span>
                      <span className="font-semibold text-gray-900">{summary?.soldLeads ?? 0} ({saleVsTotalLeadsPercentage.toFixed(1)}%)</span>
                    </div>
                    <div className="flex items-center justify-between text-gray-700">
                      <span className="font-medium">Lead Unlock</span>
                      <span className="font-semibold text-gray-900">{summary?.totalUnlockLeads ?? 0} ({unlockVsTotalLeadsPercentage.toFixed(1)}%)</span>
                    </div>
                  </div>
                  <div className="mt-2 inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    {saleVsTotalLeadsPercentage.toFixed(1)}% of all leads
                  </div>
                </div>

                <div className="rounded-2xl border border-rose-100 bg-gradient-to-br from-rose-50 to-white p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Replacement</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">Accepted requests</div>
                    </div>
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-100 text-rose-700 text-sm">
                      <i className="bi bi-arrow-repeat"></i>
                    </span>
                  </div>
                  <div className="mt-4 text-xl font-semibold text-gray-900">{summary?.acceptedReplacements ?? 0}</div>
                  <div className="mt-2 text-[11px] text-gray-700">Requests {summary?.totalReplacementRequests ?? 0}</div>
                  <div className="mt-2 text-[11px] font-semibold text-rose-700">Accepted Rs {acceptedReplacementAmount.toFixed(2)}</div>
                  <div className="mt-2 inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                    {replacementVsTotalUnlockPercentage.toFixed(1)}% of unlocked leads
                  </div>
                </div>

                <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-white p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Revenue Earned</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">Unlock - replacement</div>
                    </div>
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-700 text-sm">
                      <i className="bi bi-currency-rupee"></i>
                    </span>
                  </div>
                  <div className="mt-4 text-xl font-semibold text-gray-900">{summary?.totalRevenue ?? 0}</div>
                  <div className="mt-2 space-y-1 text-[11px]">
                    <div className="flex items-center justify-between text-gray-600">
                      <span>Unlock</span>
                      <span className="font-semibold text-gray-900">Rs {totalUnlockAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-gray-600">
                      <span>Replacement</span>
                      <span className="font-semibold text-rose-700">Rs {acceptedReplacementAmount.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-gray-500">Revenue vs est.value</span>
                    <span className="font-semibold text-amber-700">{valueRealisationPercentage.toFixed(1)}%</span>
                  </div>
                  <div className={`mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${profitPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    <i className={`bi ${profitPositive ? 'bi-arrow-up-right' : 'bi-arrow-down-right'}`}></i>
                    <span><span>{profitPositive ? 'Profit' : 'Loss'} Rs </span><span className="text-[13px] font-bold">{Math.abs(profitAmount).toFixed(2)} ({profitPercentage.toFixed(1)}%)</span></span>
                  </div>
                </div>
              </div>

              {/* <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-2.5">
                <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Lead to Sale Rate</div>
                  <div className="mt-1.5 text-base font-semibold text-gray-900">{summary?.conversionRate ?? 0}%</div>
                  <div className="text-[11px] text-gray-500">How many leads became sales</div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Unlock Rate</div>
                  <div className="mt-1.5 text-base font-semibold text-gray-900">{unlockVsTotalLeadsPercentage.toFixed(2)}%</div>
                  <div className="text-[11px] text-gray-500">Share of leads vendors opened</div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Replacement Rate</div>
                  <div className="mt-1.5 text-base font-semibold text-gray-900">{replacementVsTotalUnlockPercentage.toFixed(2)}%</div>
                  <div className="text-[11px] text-gray-500">Share of unlocked leads needing replacement</div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Average Lead Price</div>
                  <div className="mt-1.5 text-base font-semibold text-gray-900">{summary?.averageLeadPrice ?? 0}</div>
                  <div className="text-[11px] text-gray-500">Average selling price per unlocked lead</div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Revenue per Lead</div>
                  <div className="mt-1.5 text-base font-semibold text-gray-900">{summary?.revenuePerLead ?? 0}</div>
                  <div className="text-[11px] text-gray-500">Average revenue from each collected lead</div>
                </div>
                <div className={`rounded-xl border px-3 py-2.5 ${profitPositive ? 'border-emerald-100 bg-emerald-50/80' : 'border-red-100 bg-red-50/80'}`}>
                  <div className="flex items-center justify-between gap-2 text-[11px] font-medium uppercase tracking-wide">
                    <span className={profitPositive ? 'text-emerald-600' : 'text-red-600'}>Profit Trend</span>
                    <i className={`bi ${profitPositive ? 'bi-arrow-up-right' : 'bi-arrow-down-right'} ${profitPositive ? 'text-emerald-600' : 'text-red-600'}`}></i>
                  </div>
                  <div className={`mt-1.5 text-base font-semibold ${profitPositive ? 'text-emerald-700' : 'text-red-700'}`}>{profitPercentage.toFixed(1)}%</div>
                  <div className={`text-[11px] ${profitPositive ? 'text-emerald-600' : 'text-red-600'}`}>{profitPositive ? 'Above estimated value' : 'Below estimated value'}</div>
                </div>
              </div> */}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="rounded-2xl bg-white shadow-md border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900">Today Overview</h3>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700">Today</span>
                </div>
                <div className="divide-y divide-gray-100 text-sm">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-600">Today Leads</span>
                    <span className="font-semibold text-gray-900">{summary?.todayLeads ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-600">Today Unlock Leads</span>
                    <span className="font-semibold text-gray-900">{summary?.todayUnlockLeads ?? summary?.todayLeadsUnlock ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-600">Today Revenue</span>
                    <span className="font-semibold text-gray-900">{summary?.todayRevenue ?? 0}</span>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="text-gray-600">Today Replacement Requests</span>
                    <span className="font-semibold text-gray-900">{summary?.todayReplacementRequests ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="text-gray-600">Today Auto Buy</span>
                    <span className="font-semibold text-gray-900">{summary?.todayAutoBuyCount ?? 0}</span>
                  </div>
                </div>
              </div>
              <SourcePieChart data={charts?.leadsBySource || []} />
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Trend Analysis</p>
              <div className="grid grid-cols-1 gap-6">
                <LeadsLineChart data={charts?.leadsOverTime || []} />
                <RevenueAreaChart data={charts?.revenueOverTime || []} />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Vendor Insights</p>
              <div className="grid grid-cols-1 gap-6">
                <VendorPerformanceBar data={charts?.vendorPerformance || []} />
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Location Conversion</p>
              <LocationConversionTable data={charts?.locationConversion || []} />
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Top Location Performance</p>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <TopDestinationsBar data={charts?.topDestinations || []} title="Top Destinations" />
                <TopDestinationsBar data={charts?.topSourceLocations || []} title="Top Source Locations" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadAnalytics;














